import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A Scatter Plot is a data visualization tool that displays values for two variables as
          points on a two-dimensional axis. Each point represents a data point&apos;s position based
          on its values for the X and Y variables. Scatter plots are commonly used to identify
          correlations, trends, or patterns between two variables. They are particularly useful for
          detecting relationships, clusters, and outliers in the data, making them ideal for
          exploratory data analysis and regression analysis.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Scatter plots are a versatile tool for exploring relationships and patterns in data. Here
          are the most important applications:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Analyzing Correlations Between Variables:</strong> Scatter plots are ideal for
            identifying relationships or trends between two continuous variables, helping determine
            whether they are positively, negatively, or not correlated.
          </li>
          <li>
            <strong>Spotting Clusters and Outliers:</strong> These charts help visualize clusters of
            similar data points and detect outliers that deviate significantly from the overall
            pattern.
          </li>
          <li>
            <strong>Assessing Distribution and Spread:</strong> Scatter plots effectively display
            the spread of data across two dimensions, offering insights into variability and
            distribution.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Scatter plots are useful for visualizing relationships between two continuous variables,
          but they also have certain limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Data Points:</strong> In scatter plots with large datasets, data
            points may overlap, making it difficult to identify individual values. This can lead to
            a cluttered chart and obscure the true distribution or relationships within the data.
          </li>
          <li>
            <strong>Limited Insight into Data Distribution:</strong> While scatter plots show
            correlations between variables, they don&apos;t provide detailed insights into the
            overall distribution of the data. For example, outliers and clusters may not be
            immediately obvious, which could lead to an incomplete understanding of the data&apos;s
            behavior.
          </li>
          <li>
            <strong>Difficulties in Visualizing Complex Relationships:</strong> Scatter plots are
            most effective for showing linear or simple relationships between two variables. For
            more complex relationships, such as non-linear interactions, scatter plots may not
            effectively convey the underlying patterns without additional modifications like
            regression lines or color coding.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in scatter plots is vital for highlighting different data points and
          making patterns easily distinguishable. Using distinct colors for different data
          categories helps avoid confusion and enhances clarity, particularly when there are
          multiple groups or variables involved. Gradients or continuous color schemes can also be
          used to represent a third variable, such as intensity or magnitude, across the plot.
          Additionally, selecting contrasting colors for data points ensures that outliers or
          important trends stand out. Consistent color usage improves the visual appeal and
          interpretability of scatter plots, helping users draw insights effectively.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Scatter plots effectively display relationships between two variables, but they face
          certain challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overplotting:</strong> When too many data points overlap, particularly in dense
            datasets, it becomes difficult to distinguish individual points or identify patterns.
          </li>
          <li>
            <strong>Limited Dimensionality:</strong> Scatter plots excel with two variables but
            struggle to represent higher-dimensional data without additional elements like color or
            size, which can complicate interpretation.
          </li>
          <li>
            <strong>Outlier Influence:</strong> Outliers can disproportionately draw attention and
            distort the perceived relationship between variables, requiring careful preprocessing
            and analysis.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
